import { Card, ThemeProvider } from "@mui/material";
import { keepPreviousData } from "@tanstack/react-query";
import LoadingScreen from "components/loading/LoadingScreen";
import ModalConfirm from "components/modal/ModalConfirm";
import Table from "components/table/Table";
import { ALL_OPTION } from "constant";
import { useModalState } from "hooks/useModalState";
import { usePagination } from "hooks/usePagination";
import moment from "moment";
import { useMemo, useState } from "react";
import {
  useDeleteExtension,
  useExportListExtension,
  useGetListExtensionV1,
} from "service/query/extension.query";
import muiTheme from "theme/muiTheme";

import ExtensionFilter from "./components/ExtensionFilter";
import ModalExtension from "./components/ExtensionModal";

const Extension = () => {
  const [filter, setFilter] = useState(null);
  const { page, pageSize, handleChangePage, handleChangePageSize } =
    usePagination();

  const extensionModal = useModalState();
  const deleteExtensionModal = useModalState();
  const exportQuery = useExportListExtension(
    {
      ...filter,
      exportFile: true,
    },
    { enabled: false }
  );

  const [currentItem, setCurrentItem] = useState(null);

  const { data: listExtension, isFetching } = useGetListExtensionV1(
    {
      ...filter,
      page,
      size: pageSize,
    },
    { enabled: !!filter, placeholderData: keepPreviousData }
  );

  const handleSearchListExtension = (values) => {
    const { typeProvince, isDeleted, nameExtension, code } = values;

    setFilter({
      typeProvince,
      ...(isDeleted !== ALL_OPTION.value && { isDeleted }),
      ...(nameExtension && { nameExtension }),
      code: JSON.stringify(values.code.includes(ALL_OPTION.value) ? [] : code),
      exportFile: false,
    });
    handleChangePage(1);
  };

  const handleExportListExtension = () => {
    exportQuery.refetch();
  };

  const handleOpenModalEditExtension = (item) => {
    setCurrentItem(item);
    extensionModal.openModal();
  };

  const handleCloseModalExtension = () => {
    setCurrentItem(null);
    extensionModal.closeModal();
  };

  const handleCloseModalDeleteExtension = () => {
    setCurrentItem(null);
    deleteExtensionModal.closeModal();
  };

  const deleteExtensionQuery = useDeleteExtension(currentItem?._id);

  const handleDeleteExtension = () => {
    deleteExtensionQuery.mutate(null, {
      onSuccess: handleCloseModalDeleteExtension,
    });
  };

  const columns = useMemo(
    () => [
      {
        id: "cityName",
        name: "BĐT/TP",
      },
      {
        id: "subOrgName",
        name: "Bưu cục",
      },
      { id: "name", name: "Máy lẻ" },
      {
        id: "isDeleted",
        name: "Trạng thái",
        render: (record) =>
          record.isDeleted === "true" ? "Ngừng hoạt động" : "Đang hoạt động",
      },
      {
        id: "createdAt",
        name: "Thời gian khởi tạo",
        type: "datetime",
      },
      {
        id: "deletedAt",
        name: "Thời gian đóng",
        type: "datetime",
        render: (record) =>
          record.isDeleted === "true" && record.deletedAt
            ? moment(record.deletedAt).format("YYYY-MM-DD HH:mm:ss")
            : "",
      },
    ],
    []
  );

  return (
    <ThemeProvider theme={muiTheme}>
      <Card>
        <ExtensionFilter
          onSubmit={handleSearchListExtension}
          onExport={handleExportListExtension}
        />
      </Card>

      <Card sx={{ mt: 2 }}>
        <Table
          columns={columns}
          data={listExtension?.data?.docs}
          actionColumn={{
            onView: handleOpenModalEditExtension,
          }}
          toolbar={{
            title: "Danh sách máy lẻ",
          }}
          pagination={{
            page,
            pageSize,
            totalItems: listExtension?.data?.total,
            onPageChange: handleChangePage,
            onPageSizeChange: handleChangePageSize,
          }}
        />
      </Card>

      {extensionModal.open && (
        <ModalExtension
          currentItem={currentItem}
          isCreate={!currentItem}
          open={extensionModal.open}
          onClose={handleCloseModalExtension}
          mode="view"
        />
      )}

      <ModalConfirm
        onClose={deleteExtensionModal.closeModal}
        open={deleteExtensionModal.open}
        onConfirm={handleDeleteExtension}
        loading={deleteExtensionQuery.isPending}
        title="Bạn có muốn xóa máy lẻ?"
      />

      <LoadingScreen loading={isFetching || exportQuery.isFetching} />
    </ThemeProvider>
  );
};

export default Extension;
